/**
 * useConfirm Hook
 *
 * Hook para usar o ConfirmModal de forma simples e elegante
 * Substitui o window.confirm() nativo
 *
 * @example
 * const { confirm, ConfirmDialog } = useConfirm();
 *
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: 'Excluir usuário',
 *     message: 'Tem certeza que deseja excluir?',
 *     variant: 'danger',
 *     confirmText: 'Excluir',
 *   });
 *
 *   if (confirmed) {
 *     // Executar exclusão
 *   }
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleDelete}>Excluir</button>
 *     <ConfirmDialog />
 *   </>
 * );
 */

import { useState, useCallback, ReactNode } from 'react';
import { ConfirmModal, ConfirmModalVariant } from '../components/ui/ConfirmModal';

interface ConfirmOptions {
  title: string;
  message: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmModalVariant;
}

interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
  resolve: (value: boolean) => void;
}

export const useConfirm = () => {
  const [state, setState] = useState<ConfirmState | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        ...options,
        isOpen: true,
        resolve,
      });
    });
  }, []);

  const handleClose = useCallback(() => {
    if (state) {
      state.resolve(false);
      setState(null);
    }
  }, [state]);

  const handleConfirm = useCallback(() => {
    if (state) {
      state.resolve(true);
      setState(null);
    }
  }, [state]);

  const ConfirmDialog = useCallback(() => {
    if (!state) return null;

    return (
      <ConfirmModal
        isOpen={state.isOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={state.title}
        message={state.message}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        variant={state.variant}
      />
    );
  }, [state, handleClose, handleConfirm]);

  return { confirm, ConfirmDialog };
};

export default useConfirm;
